import { useEffect } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

function NotoRecoveryJsonLd() {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}/portfolio/noto-recovery#collection`,
      name: 'Recover Noto | KAZU.PHOTO – 能登復興ドキュメンタリー写真プロジェクト',
      description: '2024年より記録開始。能登の復興、海藻、手仕事をめぐり、被災地の人々の暮らしと希望を追い続けるドキュメンタリー写真・映像プロジェクト。',
      url: `${SITE_URL}/portfolio/noto-recovery`,
      inLanguage: 'ja',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ホーム', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Projects', item: `${SITE_URL}/#projects` },
          { '@type': 'ListItem', position: 3, name: 'Recover Noto', item: `${SITE_URL}/portfolio/noto-recovery` },
        ],
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'jsonld-noto-recovery';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('jsonld-noto-recovery');
      if (existing) existing.parentNode?.removeChild(existing);
    };
  }, []);

  return null;
}

const NotoRecoveryProject = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fieldNotes = [
    {
      id: 1,
      image: 'https://public.readdy.ai/ai/img_res/eec67e381fe31188fcd424342c3dd25d.jpg',
      caption: '海と向き合う日々',
      text: '能登の海は、人々の暮らしと深く結びついています。'
    },
    {
      id: 2,
      image: 'https://readdy.ai/api/search-image?query=Close%20up%20hands%20of%20Japanese%20artisan%20carefully%20processing%20fresh%20seaweed%20on%20wooden%20table%20in%20traditional%20workshop%20natural%20window%20light%20documentary%20style%20showing%20detailed%20handwork%20and%20dedication%20to%20craft%20warm%20earth%20tones%20authentic%20atmosphere%20of%20traditional%20food%20preparation&width=1200&height=800&seq=noto-field2&orientation=landscape',
      caption: '素材と向き合う',
      text: '海藻を丁寧に扱う手には、土地への敬意が宿っています。'
    },
    {
      id: 3,
      image: 'https://readdy.ai/api/search-image?query=Portrait%20of%20elderly%20Japanese%20craftsperson%20in%20Noto%20Peninsula%20smiling%20gently%20while%20working%20with%20traditional%20materials%20soft%20natural%20light%20documentary%20photography%20showing%20resilience%20and%20hope%20warm%20tones%20capturing%20human%20spirit%20and%20dedication%20to%20rebuilding%20community%20after%20disaster&width=1200&height=800&seq=noto-field3&orientation=landscape',
      caption: '人の表情',
      text: '前を向く力は、日々の小さな積み重ねから生まれます。'
    }
  ];

  const galleryImages = [
    {
      id: 1,
      url: 'https://readdy.ai/api/search-image?query=Wide%20landscape%20view%20of%20Noto%20Peninsula%20coastline%20with%20traditional%20Japanese%20fishing%20village%20houses%20scattered%20along%20shore%20misty%20morning%20light%20peaceful%20atmosphere%20documentary%20photography%20natural%20muted%20colors%20showing%20resilience%20and%20beauty%20of%20coastal%20community%20life&width=1200&height=800&seq=noto-gallery1&orientation=landscape',
      alt: '能登の海岸線',
      size: 'large'
    },
    {
      id: 2,
      url: 'https://readdy.ai/api/search-image?query=Japanese%20local%20people%20working%20together%20in%20community%20rebuilding%20effort%20Noto%20Peninsula%20natural%20documentary%20photography%20showing%20collaboration%20and%20hope%20warm%20afternoon%20light%20authentic%20moment%20of%20recovery%20and%20mutual%20support%20after%20disaster&width=1200&height=800&seq=noto-gallery2&orientation=landscape',
      alt: '復興への歩み',
      size: 'medium'
    },
    {
      id: 3,
      url: 'https://readdy.ai/api/search-image?query=Fresh%20seaweed%20varieties%20laid%20out%20on%20traditional%20Japanese%20wooden%20surface%20close%20up%20shot%20natural%20light%20documentary%20style%20showing%20rich%20textures%20and%20colors%20of%20ocean%20harvest%20earth%20tones%20emphasizing%20natural%20beauty%20and%20sustainable%20food%20culture&width=1200&height=800&seq=noto-gallery3&orientation=landscape',
      alt: '海藻の素材',
      size: 'medium'
    },
    {
      id: 4,
      url: 'https://readdy.ai/api/search-image?query=Hands%20of%20Japanese%20artisan%20preparing%20traditional%20seaweed%20based%20food%20product%20in%20workshop%20close%20up%20documentary%20photography%20soft%20natural%20light%20showing%20careful%20handwork%20and%20dedication%20to%20craft%20warm%20tones%20capturing%20essence%20of%20traditional%20food%20making&width=1200&height=800&seq=noto-gallery4&orientation=landscape',
      alt: '手仕事の現場',
      size: 'medium'
    },
    {
      id: 5,
      url: 'https://readdy.ai/api/search-image?query=Traditional%20Japanese%20fishing%20boat%20on%20calm%20sea%20near%20Noto%20Peninsula%20coast%20early%20morning%20mist%20golden%20hour%20light%20documentary%20photography%20peaceful%20atmosphere%20showing%20connection%20between%20people%20and%20ocean%20natural%20muted%20colors&width=1200&height=800&seq=noto-gallery5&orientation=landscape',
      alt: '海と暮らし',
      size: 'medium'
    },
    {
      id: 6,
      url: 'https://readdy.ai/api/search-image?query=Portrait%20of%20Japanese%20fisherman%20or%20craftsperson%20from%20Noto%20Peninsula%20looking%20toward%20ocean%20with%20hopeful%20expression%20natural%20light%20documentary%20photography%20showing%20resilience%20and%20determination%20warm%20tones%20capturing%20human%20spirit%20and%20connection%20to%20land&width=1200&height=800&seq=noto-gallery6&orientation=landscape',
      alt: '未来を見つめる',
      size: 'medium'
    }
  ];

  const handleNavigate = (path: string) => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE(path);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <NotoRecoveryJsonLd />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
          <button
            onClick={() => handleNavigate('/')}
            className="text-stone-800 hover:text-stone-600 transition-colors cursor-pointer font-light tracking-wide"
          >
            Kazuki Odake
          </button>
          <button
            onClick={() => handleNavigate('/')}
            className="text-stone-600 hover:text-stone-800 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            <span className="text-sm">Back to Home</span>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="w-full h-[80vh] overflow-hidden rounded-2xl mb-24">
            <img
              src="https://readdy.ai/api/search-image?query=Atmospheric%20wide%20shot%20of%20Noto%20Peninsula%20Japan%20coastal%20landscape%20with%20local%20people%20working%20on%20seaweed%20harvesting%20and%20processing%20in%20foreground%20traditional%20village%20and%20misty%20mountains%20in%20background%20golden%20hour%20light%20documentary%20cinematic%20style%20warm%20natural%20tones%20capturing%20essence%20of%20community%20resilience%20hope%20and%20connection%20to%20land%20after%20disaster&width=1600&height=900&seq=noto-hero&orientation=landscape"
              alt="Recover Noto - 能登の復興"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-light text-stone-800 mb-12 tracking-tight">
              Recover Noto
            </h1>
            <p className="text-base lg:text-lg text-stone-600 leading-relaxed font-light">
              2024年より記録開始。<br />
              能登の復興、海藻、手仕事をめぐり、<br />
              被災地の人々の暮らしと希望を追い続けるドキュメンタリープロジェクト。
            </p>
          </div>
        </div>
      </section>

      {/* Project Story Section */}
      <section className="py-32 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 lg:gap-20 items-center">
            <div className="lg:col-span-2">
              <h2 className="text-3xl lg:text-4xl font-light text-stone-800 mb-8 tracking-tight">
                Project Story
              </h2>
              <p className="text-base text-stone-600 leading-relaxed font-light">
                能登の海藻を活かした出汁パックやふりかけの商品開発を軸に、人・土地・文化のつながりを記録するプロジェクト。<br /><br />
                被災地の現実だけでなく、その先にある希望や創造を<br />
                丁寧に残していきます。
              </p>
            </div>
            <div className="lg:col-span-3 aspect-[4/3] rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Japanese%20artisan%20hands%20carefully%20arranging%20dried%20seaweed%20products%20on%20traditional%20wooden%20surface%20in%20Noto%20Peninsula%20workshop%20soft%20natural%20window%20light%20documentary%20photography%20warm%20earth%20tones%20showing%20dedication%20to%20craft%20and%20hope%20for%20future%20authentic%20atmosphere%20of%20traditional%20food%20culture&width=800&height=600&seq=noto-story&orientation=landscape"
                alt="プロジェクトの物語"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Field Notes Section */}
      <section className="py-32 px-6 lg:px-12 bg-stone-50">
        <div className="max-w-5xl mx-auto">
          <div className="mb-24 text-center">
            <h2 className="text-3xl lg:text-4xl font-light text-stone-800 mb-6 tracking-tight">
              Field Notes
            </h2>
            <p className="text-base text-stone-600 font-light leading-relaxed">
              海、素材、手仕事、人の表情。<br />
              能登の今を少しずつ積み重ねながら記録していくプロジェクト。
            </p>
          </div>

          <div className="space-y-32">
            {fieldNotes.map((note, index) => (
              <div
                key={note.id}
                className={`grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}
              >
                <div className={`lg:col-span-3 ${index % 2 === 1 ? 'lg:col-start-3' : ''}`}>
                  <div className="aspect-[3/2] rounded-xl overflow-hidden">
                    <img
                      src={note.image}
                      alt={note.caption}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className={`lg:col-span-2 ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                  <h3 className="text-xl font-light text-stone-800 mb-4 tracking-tight">
                    {note.caption}
                  </h3>
                  <p className="text-sm text-stone-600 leading-relaxed font-light">
                    {note.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-10">
            {/* Large Image */}
            <div className="md:col-span-8 group cursor-pointer">
              <div className="aspect-[3/2] overflow-hidden rounded-lg bg-stone-200">
                <img
                  src={galleryImages[0].url}
                  alt={galleryImages[0].alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>

            {/* Two Medium Images */}
            <div className="md:col-span-4 space-y-8 lg:space-y-10">
              <div className="group cursor-pointer">
                <div className="aspect-[3/2] overflow-hidden rounded-lg bg-stone-200">
                  <img
                    src={galleryImages[1].url}
                    alt={galleryImages[1].alt}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>
              <div className="group cursor-pointer">
                <div className="aspect-[3/2] overflow-hidden rounded-lg bg-stone-200">
                  <img
                    src={galleryImages[2].url}
                    alt={galleryImages[2].alt}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>
            </div>

            {/* Three Medium Images */}
            <div className="md:col-span-4 group cursor-pointer">
              <div className="aspect-[3/2] overflow-hidden rounded-lg bg-stone-200">
                <img
                  src={galleryImages[3].url}
                  alt={galleryImages[3].alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>
            <div className="md:col-span-4 group cursor-pointer">
              <div className="aspect-[3/2] overflow-hidden rounded-lg bg-stone-200">
                <img
                  src={galleryImages[4].url}
                  alt={galleryImages[4].alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>
            <div className="md:col-span-4 group cursor-pointer">
              <div className="aspect-[3/2] overflow-hidden rounded-lg bg-stone-200">
                <img
                  src={galleryImages[5].url}
                  alt={galleryImages[5].alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Documentary Film Section */}
      <section className="py-32 px-6 lg:px-12 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-center mb-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl lg:text-4xl font-light text-stone-800 mb-6 tracking-tight">
                Documentary Film
              </h2>
              <p className="text-base text-stone-600 font-light leading-relaxed">
                Recover Notoの歩みを、映像でも記録しています。<br />
                海藻の採取、手仕事、土地に生きる人々の時間を映像で追っています。
              </p>
            </div>
            <div className="lg:col-span-3 aspect-video bg-stone-200 rounded-2xl overflow-hidden">
              <div className="w-full h-full flex items-center justify-center text-stone-400">
                <div className="text-center">
                  <i className="ri-film-line text-5xl mb-4" />
                  <p className="text-sm font-light">Coming Soon</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="py-32 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-light text-stone-800 mb-16 tracking-tight text-center">
            Journey
          </h2>
          <div className="space-y-8">
            <div className="border-l-2 border-stone-300 pl-8 py-2">
              <p className="text-sm text-stone-500 mb-2 font-light">2024年1月</p>
              <p className="text-base text-stone-700 font-light">記録開始</p>
            </div>
            <div className="border-l-2 border-stone-300 pl-8 py-2">
              <p className="text-sm text-stone-500 mb-2 font-light">2024年春</p>
              <p className="text-base text-stone-700 font-light">海藻採取の現場撮影</p>
            </div>
            <div className="border-l-2 border-stone-300 pl-8 py-2">
              <p className="text-sm text-stone-500 mb-2 font-light">2024年夏</p>
              <p className="text-base text-stone-700 font-light">商品開発の過程を記録</p>
            </div>
            <div className="border-l-2 border-stone-300 pl-8 py-2">
              <p className="text-sm text-stone-500 mb-2 font-light">継続中</p>
              <p className="text-base text-stone-700 font-light">関わる人々の手仕事と暮らしを撮影中</p>
            </div>
          </div>
        </div>
      </section>

      {/* Project Information */}
      <section className="py-32 px-6 lg:px-12 bg-stone-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 text-center md:text-left">
            <div>
              <p className="text-xs text-stone-500 mb-3 uppercase tracking-wider font-light">
                場所
              </p>
              <p className="text-base text-stone-800 font-light">
                石川県・能登
              </p>
            </div>
            <div>
              <p className="text-xs text-stone-500 mb-3 uppercase tracking-wider font-light">
                種別
              </p>
              <p className="text-base text-stone-800 font-light">
                Documentary Photography / Film
              </p>
            </div>
            <div>
              <p className="text-xs text-stone-500 mb-3 uppercase tracking-wider font-light">
                主題
              </p>
              <p className="text-base text-stone-800 font-light">
                復興、海藻、手仕事、暮らし、文化
              </p>
            </div>
            <div>
              <p className="text-xs text-stone-500 mb-3 uppercase tracking-wider font-light">
                状態
              </p>
              <p className="text-base text-stone-800 font-light">
                進行中のプロジェクト
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section className="py-32 px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl font-light text-stone-800 mb-16 tracking-tight text-center">
            関連プロジェクト
          </h3>
          <div
            onClick={() => handleNavigate('/portfolio/wajima-nuri')}
            className="group cursor-pointer rounded-2xl overflow-hidden bg-white hover:shadow-lg transition-all duration-500"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Atmospheric%20shot%20of%20Japanese%20master%20craftsman%20working%20on%20traditional%20Wajima%20lacquerware%20in%20wooden%20workshop%20with%20soft%20natural%20light%20documentary%20photography%20warm%20earth%20tones%20capturing%20essence%20of%20300%20year%20old%20tradition%20and%20dedication%20to%20craft&width=800&height=600&seq=wajima-related&orientation=landscape"
                  alt="輪島塗"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-12 lg:p-16 flex flex-col justify-center">
                <h4 className="text-2xl font-light text-stone-800 mb-6 tracking-tight">
                  輪島塗
                </h4>
                <p className="text-sm text-stone-600 font-light leading-relaxed">
                  300年以上続く伝統工芸。<br />
                  分業によって受け継がれる技術と、職人の静かな手仕事を記録する。
                </p>
                <div className="flex items-center gap-2 text-stone-800 group-hover:gap-4 transition-all mt-8">
                  <span className="text-sm font-light whitespace-nowrap">View Project</span>
                  <i className="ri-arrow-right-line" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-100 py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-sm text-stone-600 font-light">
              © 2024 Kazuki Odake Photography. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-stone-600 hover:text-stone-800 transition-colors cursor-pointer"
              >
                <i className="ri-instagram-line text-xl" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-stone-600 hover:text-stone-800 transition-colors cursor-pointer"
              >
                <i className="ri-twitter-x-line text-xl" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NotoRecoveryProject;